import { ICommonSettings, IMSALSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { ReactNode } from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSwitch from "../../../../ui-kit/kuika-switch/kuika-switch";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigMSALSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingMsal?: IMSALSetting;
}

export interface IConfigMSALSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingMsalId: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigMSALSettings extends React.PureComponent<IConfigMSALSettingsProps, IConfigMSALSettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigMSALSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingMsal: undefined
    };
  }

  componentDidMount() {
    this.loadMsalSettings();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigMSALSettingsProps) {
    if (prevProps.editingMsalId && !this.props.editingMsalId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingMsalId !== this.props.editingMsalId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadMsalSettings();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadMsalSettings();
    }
  }

  loadMsalSettings = () => {
    if (!this.props.editingMsalId) return;

    const msalSettings = _.cloneDeep(this.props.commonSettings?.msalSettings);
    const editingMsal = msalSettings?.find((setting) => setting.id === this.props.editingMsalId);
    if (editingMsal)
      this.setState({ editingMsal }, () => {
        this.formRef.current?.resetFields();
        this.formRef.current?.setFieldsValue(editingMsal);
      });
  };

  existingNameValidator = async (_rule, value) => {
    const msalSettings = this.props.commonSettings?.msalSettings;

    const isSettingAlreadyExist =
      msalSettings?.some((x) => x.name === value && x.id !== this.props.editingMsalId) || value === "Default";

    if (isSettingAlreadyExist) throw new Error("MSAL Setting Name is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    const obj: IMSALSetting = {
      id:
        this.props.editingMsalId && this.props.editingMsalId !== ""
          ? this.props.editingMsalId
          : Guid.create().toString(),
      name: data.name,
      clientId: data.clientId?.trim(),
      clientSecret: data.clientSecret?.trim(),
      authority: data.authority?.trim(),
      scopes: data.scopes?.trim(),
      keystoreBase64: data.keystoreBase64?.trim(),
      cacheLocation: data.cacheLocation?.trim(),
      cacheLocationEnabled: data.cacheLocationEnabled
    };
    if (this.props.editingMsalId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "msalSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "msalSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteMsal = () => {
    if (!this.props.editingMsalId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingMsalId || "",
      configKey: "msalSettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`MSAL setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="MSAL Setting"
        secondHeader="MSAL Setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingMsalId ? this.deleteMsal : undefined}
            deleteTestId="msalSettingDeleteButton"
            submitTestId="msalSettingCreateButton"
            submitLabel={this.props.editingMsalId ? "UPDATE" : "CREATE"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configMsalForm"
          />
        }
      >
        <Form
          id="configMsalForm"
          name="msalSettings"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("appsettings_msalsettingpop_up_description")}>
            <div>
              <ConfigField label="Name" required tooltipContentKey="appsettings_msalsettingpop_up_name">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Please enter a MSAL Setting Name." },
                    { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "MSAL Setting Name is invalid" },
                    { validator: this.existingNameValidator }
                  ]}
                >
                  <KuikaInput size="md" data-testid="msalSettingNameInput" autoFocus />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Client ID" required tooltipContentKey="appsettings_msalsettingpop_up_clientid">
                <Form.Item name="clientId" rules={[{ required: true, message: "Please enter a MSAL Client ID." }]}>
                  <KuikaInput size="md" data-testid="msalSettingClientIdInput" />
                </Form.Item>
              </ConfigField>
              <ConfigField
                label="Client Secret"
                required
                tooltipContentKey="appsettings_msalsettingpop_up_clientsecret"
              >
                <Form.Item
                  name="clientSecret"
                  rules={[{ required: true, message: "Please enter a MSAL Client Secret." }]}
                >
                  <KuikaInput size="md" data-testid="msalSettingClientSecretInput" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Authority" tooltipContentKey="appsettings_msalsettingpop_up_authority">
                <Form.Item name="authority">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Scopes" tooltipContentKey="appsettings_msalsettingpop_up_scopes">
                <Form.Item name="scopes">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Keystore Base64" tooltipContentKey="appsettings_msalsettingpop_up_keystorebase64">
                <Form.Item name="keystoreBase64">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
              <ConfigField label="Cache Location" tooltipContentKey="appsettings_msalsettingpop_up_cachelocation">
                <Form.Item name="cacheLocationEnabled" valuePropName="checked">
                  <KuikaSwitch />
                </Form.Item>
              </ConfigField>
              <ConfigField
                label="Keystore Hash (Android only)"
                tooltipContentKey="appsettings_msalsettingpop_up_keystorehash"
              >
                <Form.Item name="cacheLocation">
                  <KuikaInput size="md" />
                </Form.Item>
              </ConfigField>
            </div>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
